/**
 * Sync date conditions: one `PropertyFilter` per date column.
 *
 *   column   → decided by the row; the key of the outer map
 *   property → which bound: from (SQL `>=`) / to (SQL `<`)
 *   operator → the kind of value: relative (year anchor) / absolute (literal date)
 *   value    → a string either way, so state and payload are the same shape
 *
 *   { close_date: { operation: 'and', tokens: [
 *       { propertyKey: 'from', operator: 'relative', value: '0' },
 *       { propertyKey: 'to',   operator: 'relative', value: '1' } ] } }
 *
 * A column's own bounds are AND'd; columns are OR'd. A column absent from the map does not
 * participate — which is NOT the same as unrestricted.
 *
 * This is the wire shape too, so the page sends its state unchanged. The control can still
 * express things the service will not run; `describeUnsupported` names them, and the handler
 * checks again because a column name reaches a COLUMN POSITION in the generated SQL.
 */

import { useEffect, useState } from 'react';
import type { SyncDateColumnQuery } from '../../client/IvtHubClient';
import {
  DateInput,
  FormField,
  Select,
  type PropertyFilterProps,
  type SelectProps,
} from '@amzn/awsui-components-react';

/** The three columns the service compiles. Mirrors Smithy's `SyncDateColumn`. */
export const DATE_COLUMNS = ['close_date', 'close_staged_date', 'created_date'] as const;
export type DateColumn = (typeof DATE_COLUMNS)[number];

/**
 * The two properties inside a row: which bound.
 *
 * The column comes from the row, so offering it as the property would mean picking from a
 * one-item list every time. `from` becomes `>=` and `to` becomes `<` — half-open, because
 * the source columns carry a time part.
 */
export const BOUNDARIES = ['from', 'to'] as const;
export type Boundary = (typeof BOUNDARIES)[number];

/** Whether the bound's value is a relative anchor or a literal date. */
export const SUPPORTED_OPERATORS = ['relative', 'absolute'] as const;

/** Conditions per column. A missing key means the column does not participate. */
export type WindowConditions = Partial<Record<DateColumn, SyncDateColumnQuery>>;

/** Relative anchors. Negative goes back, positive forward; 0 is the start of this year. */
const ANCHOR_OPTIONS: SelectProps.Option[] = [
  { value: '1', label: 'start of next year', description: 'the usual upper bound' },
  { value: '0', label: 'start of this year' },
  { value: '-1', label: 'start of last year' },
  { value: '-2', label: 'start of 2 years ago' },
  { value: '-3', label: 'start of 3 years ago' },
  { value: '-5', label: 'start of 5 years ago' },
  { value: '-10', label: 'start of 10 years ago' },
];

function anchorLabel(offset: string): string {
  const n = Number(offset);
  return (
    ANCHOR_OPTIONS.find((o) => o.value === offset)?.label ??
    `start of this year ${n > 0 ? '+' : ''}${offset}`
  );
}

/**
 * Anchor picker. Stores the offset rather than a resolved date, so the bound still means the
 * same thing next January.
 */
function AnchorForm({ value, onChange }: PropertyFilterProps.ExtendedOperatorFormProps<string>) {
  const [offset, setOffset] = useState<string | null>(value ?? null);

  useEffect(() => {
    setOffset(value ?? null);
  }, [value]);

  // null until something is picked, so Apply cannot create an empty token.
  useEffect(() => {
    onChange(offset);
  }, [offset, onChange]);

  return (
    <FormField>
      <Select
        selectedOption={ANCHOR_OPTIONS.find((o) => o.value === offset) ?? null}
        options={ANCHOR_OPTIONS}
        placeholder="Pick a year start"
        ariaLabel="Relative year anchor"
        expandToViewport
        // A string, not a number: on the wire this slot holds both an offset and an ISO
        // date, and Smithy has no untagged union. Keeping it a string keeps state and
        // payload identical.
        onChange={({ detail }) => setOffset(detail.selectedOption.value ?? null)}
      />
    </FormField>
  );
}

/**
 * Literal-date editor. `DateInput` rather than `DatePicker`: this form is already inside a
 * popover, and nesting a calendar popover under `expandToViewport` mis-stacks. A partial
 * value (`2026-`) pushes null, so Apply cannot create a broken token.
 */
function LiteralDateForm({ value, onChange }: PropertyFilterProps.ExtendedOperatorFormProps<string>) {
  const [text, setText] = useState<string>(value ?? '');

  useEffect(() => {
    setText(value ?? '');
  }, [value]);

  useEffect(() => {
    onChange(/^\d{4}-\d{2}-\d{2}$/.test(text) ? text : null);
  }, [text, onChange]);

  return (
    <FormField>
      <DateInput
        value={text}
        placeholder="YYYY/MM/DD"
        ariaLabel="Fixed date"
        onChange={({ detail }) => setText(detail.value)}
      />
    </FormField>
  );
}

/** `format` is what the token shows; without it the raw value (`-1`) is what appears. */
const OPERATORS: PropertyFilterProps.FilteringProperty['operators'] = [
  {
    operator: 'relative',
    form: AnchorForm,
    format: (v) => anchorLabel(String(v)),
    description: 'Relative — rolls forward every year',
  },
  {
    operator: 'absolute',
    form: LiteralDateForm,
    format: (v) => String(v),
    description: 'Fixed date — does not roll forward',
  },
];

/** The row's properties. `to` says "exclusive" in its label because the SQL is `<`. */
export const BOUNDARY_PROPERTIES: PropertyFilterProps.FilteringProperty[] = [
  {
    key: 'from',
    propertyLabel: 'From',
    groupValuesLabel: 'Lower bound',
    operators: OPERATORS,
    defaultOperator: 'relative',
  },
  {
    key: 'to',
    propertyLabel: 'To (exclusive)',
    groupValuesLabel: 'Upper bound',
    operators: OPERATORS,
    defaultOperator: 'relative',
  },
];

type Leaf = { propertyKey?: string; operator: string; value: unknown };

function leaves(query: PropertyFilterProps.Query | undefined): Leaf[] {
  if (!query) return [];
  const out: Leaf[] = [];
  const walk = (nodes: readonly unknown[]) => {
    for (const n of nodes) {
      if (n && typeof n === 'object' && 'operation' in n && 'tokens' in n) {
        walk((n as { tokens: readonly unknown[] }).tokens);
      } else if (n && typeof n === 'object' && 'operator' in n) {
        out.push(n as Leaf);
      }
    }
  };
  walk(query.tokens ?? []);
  walk(query.tokenGroups ?? []);
  return out;
}

/**
 * A row's query, flattened to a plain AND. Nesting has no compile target, and this is
 * idempotent so it can sit in `onChange`.
 */
export function normalizeColumnQuery(query: PropertyFilterProps.Query): SyncDateColumnQuery {
  return {
    operation: 'and',
    // Cast at the boundary: the control's token is untyped, and anything outside the
    // enums is surfaced by describeUnsupported rather than dropped here.
    tokens: leaves(query).map((t) => ({
      propertyKey: t.propertyKey,
      operator: t.operator,
      value: String(t.value),
    })) as SyncDateColumnQuery['tokens'],
  };
}

/**
 * The wire shape as the control wants it. The generated members are all optional, so the
 * defaults are supplied here rather than asserted away.
 */
export function toPropertyFilterQuery(
  q: SyncDateColumnQuery | undefined,
): PropertyFilterProps.Query {
  return {
    operation: 'and',
    tokens: (q?.tokens ?? []).map((t) => ({
      propertyKey: t.propertyKey,
      operator: t.operator ?? 'relative',
      value: t.value,
    })) as PropertyFilterProps.Query['tokens'],
  };
}

/** Whether the column has any conditions. None means it does not participate. */
export function columnHasConditions(conditions: WindowConditions, column: DateColumn): boolean {
  return (conditions[column]?.tokens ?? []).length > 0;
}

/** Initial value for an empty row. */
export const EMPTY_COLUMN_QUERY: PropertyFilterProps.Query = { operation: 'and', tokens: [] };

/** What the sync runs today: every column from the start of this year to the next. */
export function todaysWindowConditions(): WindowConditions {
  const both: SyncDateColumnQuery = {
    operation: 'and',
    tokens: [
      { propertyKey: 'from', operator: 'relative', value: '0' },
      { propertyKey: 'to', operator: 'relative', value: '1' },
    ],
  };
  return Object.fromEntries(DATE_COLUMNS.map((c) => [c, both])) as WindowConditions;
}

/**
 * Names each condition the service will not run, rather than returning a boolean.
 *
 * Nothing is dropped silently here — unlike `industryNodeQuery`, where a dropped token
 * disappears from the box in front of the user. This is about to be saved.
 */
export function describeUnsupported(conditions: WindowConditions): string[] {
  const problems: string[] = [];

  for (const column of DATE_COLUMNS) {
    const tokens = conditions[column]?.tokens ?? [];
    const counts = new Map<string, number>();

    for (const t of tokens) {
      if (!t.propertyKey || !(BOUNDARIES as readonly string[]).includes(t.propertyKey)) {
        problems.push(`${column}: unknown bound "${t.propertyKey ?? ''}".`);
        continue;
      }
      if (!t.operator || !(SUPPORTED_OPERATORS as readonly string[]).includes(t.operator)) {
        problems.push(`${column}: operator "${t.operator ?? ''}" is not supported by the service.`);
      }
      // Mirrors the handler: a relative bound carrying a date passes every structural
      // check and then matches nothing.
      if (t.operator === 'relative' && !/^-?\d{1,2}$/.test(String(t.value))) {
        problems.push(`${column}: a relative bound needs a year offset, got "${String(t.value)}".`);
      }
      if (t.operator === 'absolute' && !/^\d{4}-\d{2}-\d{2}$/.test(String(t.value))) {
        problems.push(`${column}: a fixed bound needs an ISO date, got "${String(t.value)}".`);
      }
      counts.set(t.propertyKey, (counts.get(t.propertyKey) ?? 0) + 1);
    }

    // Two bounds on the same end are ANDed, so only the narrowest takes effect — and
    // nothing errors.
    for (const b of BOUNDARIES) {
      const n = counts.get(b) ?? 0;
      if (n > 1) {
        problems.push(`${column} has ${n} "${b}" bounds; only the narrowest takes effect.`);
      }
    }
  }

  return problems;
}
